// ── AR Flow — NOVA INTERFACE: 📋 Rotina do gerente (Meu Dia) ─────────────────
// Três momentos do dia, cada um com os passos e os números de cada consultor.
// O ✓ de cada passo fica só neste aparelho (é um lembrete do gerente) e zera
// no dia seguinte. Só leitura no servidor: busca /api/gerente/rotina.

import { useEffect, useState } from "react";
import { lerJsonPontos } from "./PontosCard";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";

export const MOMENTOS = [
  { id: "abertura", titulo: "☀️ Abertura", ate: "até 10h30", passos: [
    { id: "esperando15h", texto: "Zerar quem espera resposta há mais de 15h", campo: "esperando15h" },
    { id: "pendencias24h", texto: "Cobrar as pendências do LIFT com mais de 24h", campo: "pendencias24h" },
    { id: "filaOntem", texto: "Tombamento de ontem: cobrar o que ficou sem fazer e sem justificativa", campo: "filaOntemSemMotivo" },
  ] },
  { id: "meio", titulo: "🌤️ Meio do dia", ate: "até 15h", passos: [
    { id: "indicacoes", texto: "Indicações sem primeiro contato no prazo", campo: "indicacoesVencidas" },
    { id: "posvenda", texto: "Pós-venda atrasado (é quando pedimos avaliação e indicação)", campo: "posVendaAtrasada" },
    { id: "entregas", texto: "Entregas com aviso: conferir com o depósito", link: "entregas" },
  ] },
  { id: "fechamento", titulo: "🌙 Fechamento", ate: "até 21h30", passos: [
    { id: "filaHoje", texto: "Tombamento de hoje: quem ainda não terminou", campo: "filaHoje" },
    { id: "coach", texto: "Uma conversa de 1 minuto com cada consultor (a sugestão está abaixo)", campo: null },
  ] },
];

function chaveDoDia(hoje, uid) { return `arflow_rotina_${hoje}_${uid}`; }
function lerFeitos(chave) {
  try { return JSON.parse(window.localStorage.getItem(chave) || "{}") || {}; } catch { return {}; }
}
function gravarFeitos(chave, v) {
  try { window.localStorage.setItem(chave, JSON.stringify(v)); } catch { /* só um lembrete local */ }
}

// Quem precisa de atenção em cada passo.
export function pendentesDoPasso(passo, porConsultor = []) {
  if (!passo.campo) return [];
  if (passo.campo === "filaHoje") return porConsultor.filter(l => l.filaHoje && l.filaHoje.feitos < l.filaHoje.total)
    .map(l => ({ id: l.id, nome: l.nome, valor: `${l.filaHoje.feitos} de ${l.filaHoje.total}` }));
  return porConsultor.filter(l => (l[passo.campo] || 0) > 0).map(l => ({ id: l.id, nome: l.nome, valor: l[passo.campo] }));
}

export default function RotinaGerente({ theme, currentUser, onIrPara }) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [feitos, setFeitos] = useState({});
  const lojaGerente = currentUser?.role === "manager" ? currentUser.storeId : null;

  useEffect(() => {
    let vivo = true;
    fetch("/api/gerente/rotina", { credentials: "include" })
      .then(lerJsonPontos)
      .then(d => { if (!vivo) return; setDados(d); setFeitos(lerFeitos(chaveDoDia(d.hoje, currentUser?.id))); })
      .catch(e => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [currentUser?.id]);

  const marcar = id => {
    const novo = { ...feitos, [id]: !feitos[id] };
    setFeitos(novo);
    if (dados) gravarFeitos(chaveDoDia(dados.hoje, currentUser?.id), novo);
  };
  const lista = dados?.porConsultor || [];
  const total = MOMENTOS.reduce((n, m) => n + m.passos.length, 0);
  const marcados = MOMENTOS.reduce((n, m) => n + m.passos.filter(p => feitos[p.id]).length, 0);

  return (
    <section style={{ background: "#fff", border: `1px solid ${line}`, borderTop: `3px solid ${th.accent}`, borderRadius: 4, marginBottom: 16, maxWidth: 880 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${line}` }}>
        <h2 style={{ margin: 0, fontFamily: DISPLAY, fontSize: 17, color: th.text }}>📋 Rotina do gerente</h2>
        <span style={{ fontFamily: MONO, fontSize: 14, color: th.accent }}>{marcados} de {total}</span>
      </div>
      {erro && <div role="alert" style={{ padding: "12px 16px", fontSize: 13, color: "#991B1B" }}>{erro}</div>}
      {!dados && !erro && <div style={{ padding: "12px 16px", fontSize: 13, color: th.muted }}>Montando a rotina de hoje…</div>}
      {dados && MOMENTOS.map(m => (
        <div key={m.id} style={{ borderBottom: `1px solid ${line}`, padding: "10px 16px" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
            <strong style={{ fontSize: 15, color: th.text }}>{m.titulo}</strong>
            <span style={{ fontSize: 12, color: th.muted }}>{m.ate}</span>
          </div>
          {m.passos.map(p => {
            const quem = pendentesDoPasso(p, lista);
            const tudoCerto = p.campo && p.campo !== null && quem.length === 0;
            return (
              <div key={p.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0" }}>
                <input type="checkbox" aria-label={`Feito: ${p.texto}`} checked={Boolean(feitos[p.id])} onChange={() => marcar(p.id)} style={{ marginTop: 3 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: th.text, textDecoration: feitos[p.id] ? "line-through" : "none" }}>
                    {p.texto}
                    {p.link && onIrPara && <button type="button" onClick={() => onIrPara(p.link)} style={{ marginLeft: 8, background: "none", border: "none", color: th.accent, fontWeight: 700, fontSize: 13, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Abrir</button>}
                  </div>
                  {tudoCerto && <div style={{ fontSize: 12, color: "#067647" }}>Tudo em dia ✓</div>}
                  {quem.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 3 }}>
                      {quem.map(q => <span key={q.id} style={{ fontSize: 12, background: "#FEF3F2", color: "#B42318", borderRadius: 99, padding: "1px 8px" }}>{q.nome} · {q.valor}</span>)}
                    </div>
                  )}
                  {p.id === "coach" && (
                    <div style={{ marginTop: 4 }}>
                      {lista.map(l => (
                        <div key={l.id} style={{ fontSize: 13, padding: "3px 0", color: th.text }}>
                          <strong>{l.nome}</strong> <span style={{ fontFamily: MONO, fontSize: 12, color: th.muted }}>({l.pontosMes} pts)</span>: {l.conversa}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {dados && !Array.isArray(dados.transferencias) && <div role="status" style={{padding:"12px 16px",fontSize:12,color:th.muted}}>
        A lista de transferências ainda não foi confirmada pelo servidor.
      </div>}
      {dados && Array.isArray(dados.transferencias) && (
        <div style={{padding:"12px 16px", borderBottom:`1px solid ${line}`}}>
          <h3 style={{fontSize:14, margin:"0 0 8px", color:th.text}}>Transferências de leads — mês atual</h3>
          <div style={{fontSize:11,color:th.muted,marginBottom:6}}>Repasses confirmados após esta atualização. O histórico anterior continua nas conversas.</div>
          {dados.totalTransferencias>50 && <div style={{fontSize:11,color:th.muted}}>Exibindo as 50 transferências mais recentes.</div>}
          {(dados.transferencias || []).filter(entry => !lojaGerente
            || [entry.fromStoreId, entry.toStoreId].map(Number).includes(Number(lojaGerente))).map(entry =>
            <div key={entry.id} style={{fontSize:12, padding:"8px 0", color:th.text}}>
              <strong>{entry.leadName}</strong> · {entry.fromName} → {entry.toName}
              <div style={{color:th.muted}}>Loja {entry.fromStoreId} → Loja {entry.toStoreId} · {new Date(entry.at).toLocaleString("pt-BR", {timeZone:"America/Sao_Paulo"})}</div>
              {entry.reason && <div>Motivo: {entry.reason}</div>}
              {entry.nextContactDate && <div>Próximo contato ao transferir: {entry.nextContactDate.split("-").reverse().join("/")}</div>}
            </div>)}
          {!(dados.transferencias || []).some(entry => !lojaGerente
            || [entry.fromStoreId, entry.toStoreId].map(Number).includes(Number(lojaGerente)))
            && <div style={{fontSize:12, color:th.muted}}>Nenhuma transferência registrada neste mês.</div>}
        </div>
      )}
      {dados && (
        <div style={{ padding: "10px 16px", fontSize: 13, color: th.muted, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span>⭐ QR code de avaliação para o balcão:</span>
          {(lojaGerente ? [lojaGerente] : dados.lojas || []).map(sid => (
            <a key={sid} href={`/api/avaliacao/cartaz/${sid}`} target="_blank" rel="noreferrer" style={{ color: th.accent, fontWeight: 700 }}>
              {lojaGerente ? "imprimir" : `loja ${sid}`}
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
